import type { PersonalNotificationsPage, NotificationPreferenceKind, NotificationEntity, NotificationSection, NotificationSnoozeFilter } from "../personalNotifications";
export const PERSONAL_NOTIFICATIONS_REQUEST_TIMEOUT_MS = 15_000;
export class PersonalNotificationsError extends Error {
  constructor(public readonly accessDenied = false, public readonly timedOut = false) { super("Não foi possível atualizar suas notificações."); }
}
export type PersonalNotificationAction =
  | { type: "read"; id: string; read: boolean }
  | { type: "read_entity"; entityType: NotificationEntity; entityId: string; read: boolean; ids: string[] }
  | { type: "snooze"; entityType: NotificationEntity; entityId: string; until: string | null }
  | { type: "follow"; entityType: "issue"; entityId: string; following: boolean }
  | { type: "preference"; enabled?: boolean; kind?: NotificationPreferenceKind; kindEnabled?: boolean }
  | { type: "retry"; id: string };
export async function requestPersonalNotifications(
  workspaceId: string, userId: string,
  options: { signal?: AbortSignal; unread?: boolean; section?: NotificationSection; snoozed?: NotificationSnoozeFilter; cursor?: { before: string; beforeId: string } | null;
    action?: PersonalNotificationAction } = {},
  request: typeof fetch = fetch,
): Promise<PersonalNotificationsPage | null> {
  const query = new URLSearchParams({ userId });
  if (options.unread) query.set("unread", "1");
  if (options.section) query.set("section", options.section);
  if (options.snoozed) query.set("snoozed", options.snoozed);
  if (options.cursor) { query.set("before", options.cursor.before); query.set("beforeId", options.cursor.beforeId); }
  if (options.signal?.aborted) throw new PersonalNotificationsError();
  const controller = new AbortController();
  let timer: ReturnType<typeof setTimeout> | undefined;
  let cancel: () => void = () => {};
  const interrupted = new Promise<never>((_resolve, reject) => {
    cancel = () => { reject(new PersonalNotificationsError()); controller.abort(); };
    options.signal?.addEventListener("abort", cancel, { once: true });
    timer = setTimeout(() => {
      // A PATCH may already have reached the server. Bound the wait without
      // retrying it or claiming that an aborted response means no write.
      reject(new PersonalNotificationsError(false, true)); controller.abort();
    }, PERSONAL_NOTIFICATIONS_REQUEST_TIMEOUT_MS);
  });
  try {
    return await Promise.race([interrupted, (async () => {
      const response = await request(`/api/workspaces/${encodeURIComponent(workspaceId)}/notifications?${query}`, {
        method: options.action ? "PATCH" : "GET", cache: "no-store", signal: controller.signal,
        ...(options.action ? { headers: { "content-type": "application/json" }, body: JSON.stringify(options.action) } : {}),
      });
      if (controller.signal.aborted) throw new PersonalNotificationsError();
      if (!response.ok) throw new PersonalNotificationsError(response.status === 401 || response.status === 403);
      const result = options.action ? null : await response.json() as PersonalNotificationsPage;
      if (controller.signal.aborted) throw new PersonalNotificationsError();
      return result;
    })()]);
  } finally {
    clearTimeout(timer);
    options.signal?.removeEventListener("abort", cancel);
  }
}
